import axios from "axios";

export type JiraConnection = {
    readonly id: string;
    name: string;
    url: string;
    userName: string | null;
    hasPassword: boolean;
    hasAccessToken: boolean;
    createdAt: string;
    updatedAt: string;
};

export type JiraConnectionInput = {
    name: string;
    url: string;
    userName?: string | null;
    // Write-only: leave empty on update to keep the stored secret unchanged.
    password?: string | null;
    accessToken?: string | null;
};

export type JiraConverterHealth = {
    url: string | null;
    healthy: boolean;
    error?: string | null;
};

export type JiraProjectSummary = {
    key: string;
    name: string;
};

export type JiraCustomFieldSummary = {
    id: string;
    name: string;
    // Null when the field cannot be imported, in which case reason says why.
    type: string | null;
    reason: string | null;
};

export type JiraSyncApi = {
    getConnections(): Promise<JiraConnection[]>;
    createConnection(input: JiraConnectionInput): Promise<JiraConnection>;
    updateConnection(id: string, input: JiraConnectionInput): Promise<JiraConnection>;
    deleteConnection(id: string): Promise<void>;
    testConnection(id: string): Promise<void>;
    preview(id: string): Promise<JiraProjectSummary[]>;
    getProjectCustomFields(id: string, projectKey: string): Promise<JiraCustomFieldSummary[]>;
    sync(
        connectionId: string,
        projectKeys: string[],
        customFieldIds: Record<string, string[]>
    ): Promise<string>;
    resyncDescriptions(connectionId: string, projectKeys: string[]): Promise<string>;
    getConverterUrl(): Promise<string | null>;
    updateConverterUrl(url: string): Promise<void>;
    getConverterHealth(): Promise<JiraConverterHealth>;
};

export const jiraSyncApi: JiraSyncApi = {
    async getConnections() {
        const { data } = await axios.get<JiraConnection[]>("/api/v0/admin/jira/connections");

        return data;
    },
    async createConnection(input) {
        const { data } = await axios.post<JiraConnection>("/api/v0/admin/jira/connections", input);

        return data;
    },
    async updateConnection(id, input) {
        const { data } = await axios.put<JiraConnection>(
            `/api/v0/admin/jira/connections/${id}`,
            input
        );

        return data;
    },
    async deleteConnection(id) {
        await axios.delete(`/api/v0/admin/jira/connections/${id}`);
    },
    async testConnection(id) {
        await axios.post(`/api/v0/admin/jira/connections/${id}/test`);
    },
    async preview(id) {
        const { data } = await axios.post<JiraProjectSummary[]>(
            `/api/v0/admin/jira/connections/${id}/preview`
        );

        return data;
    },
    async getProjectCustomFields(id, projectKey) {
        const { data } = await axios.get<JiraCustomFieldSummary[]>(
            `/api/v0/admin/jira/connections/${id}/projects/${encodeURIComponent(projectKey)}/fields`
        );

        return data;
    },
    async sync(connectionId, projectKeys, customFieldIds) {
        const { data } = await axios.post<{ sessionId: string }>("/api/v0/admin/jira/sync", {
            connectionId,
            projectKeys,
            customFieldIds,
        });

        return data.sessionId;
    },
    async resyncDescriptions(connectionId, projectKeys) {
        const { data } = await axios.post<{ sessionId: string }>(
            "/api/v0/admin/jira/resync-descriptions",
            { connectionId, projectKeys }
        );

        return data.sessionId;
    },
    async getConverterUrl() {
        const { data } = await axios.get<{ url: string | null }>("/api/v0/admin/jira/converter");

        return data.url;
    },
    async updateConverterUrl(url) {
        await axios.put("/api/v0/admin/jira/converter", { url });
    },
    async getConverterHealth() {
        const { data } = await axios.get<JiraConverterHealth>(
            "/api/v0/admin/jira/converter/health"
        );

        return data;
    },
};
